import { useCallback, useContext, useEffect, useState } from 'react';

import { useMutation } from '@apollo/client';

import ButtonBase from '@mui/material/ButtonBase';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { debounce } from 'lodash';
import { useAppDispatch, useAppSelector } from '../../app/store/hooks';
import { userSelector } from '../../app/main/mainSlice';
import { SelectedDateSelector, SelectedMealTypeSelector, UserMealPlanDataSelector } from '../../app/mealPlan/mealPlan.slice';
import { INSERT_MEAL_PLAN, INSERT_MEALS_MANY, INSERT_SHOPPING_LIST_FOR_USER, INSERT_USER_TO_MEAL_PLAN } from '../../app/graphql/mutations';
import { formatMealDate, getMealTime } from './utils';
import { useHistory } from 'react-router-dom';
import styles from './RecipeForm.module.css';
import { RightArrow } from '../../utils/iconRefs/recipePageIcons';
import Loader from '../../pages/mealPlan/Loader';
import { GoBackIcon } from '../../utils/iconRefs/recipeRatingIcons';
import { SearchRecipeIcon } from '../../iconRef/mealPLanIcons';
import SearchResultCard from './searchResultCard/SearchResultCard';
import useSearch from '../../hooks/useSearch';
import Filter from './filter/filterPage';
import { ErrorManagementContext } from '../../utils/errorManagement/ErrorManagementProvider';
import useLogRecipeEdit from '../../hooks/useLogRecipeEdit';
import useUpdateGroceryList from '../../hooks/groceryList/useUpdateGroceryList';
import { setGroceryListId } from '../../app/groceryList/groceryList.slice';
import useFetchGroceryListId from '../../hooks/useFetchGroceryListId';

interface Recipe {
    id: string;
    metadata: {
        cookingTime: number;
        cuisine: string[];
        diet: string[];
        maxServing: number;
        minServing: number;
        name: string;
        prepTime: number;
        url: string;
    };
}

const RecipeForm = ({ selectedRecipeIds, setSelectedRecipeIds }: { selectedRecipeIds: Map<string, { servingSize: number }>, setSelectedRecipeIds: React.Dispatch<React.SetStateAction<Map<string, { servingSize: number }>>> }) => {
    const [addMealPlanDB] = useMutation(INSERT_MEAL_PLAN);
    const [addUserToMealPlanDB] = useMutation(INSERT_USER_TO_MEAL_PLAN);
    const [addMealsToDB] = useMutation(INSERT_MEALS_MANY);
    const [addShoppingListForUser] = useMutation(INSERT_SHOPPING_LIST_FOR_USER);
    const { updateGroceryListFromAddingMeal } = useUpdateGroceryList();
    const { cookClubLog } = useContext(ErrorManagementContext);
    
    const selectedMealType = useAppSelector(SelectedMealTypeSelector);
    const selectedDate = useAppSelector(SelectedDateSelector);

    const user = useAppSelector(userSelector)
    const userMealPlanData = useAppSelector(UserMealPlanDataSelector)
    const [mealPlanId, setMealPlanId] = useState(userMealPlanData?.meal_plan_id ?? null);
    const [addingMealToDB, setAddingMealToDB] = useState(false);
    const { logRecipeEdit } = useLogRecipeEdit();
    const { groceryListId } = useFetchGroceryListId();

    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const dispatch = useAppDispatch()
    const history = useHistory();
    const { drawerOpen,
        handleDrawerClose,
        handleDrawerOpen,
        setSearchString,
        searchFilters,
        localSelected,
        searchQueryString,
        recipeList,
        setRecipeList,
        clearFilter,
        applySearchFilters,
        setLocalCuisine,
        setLocalIngredient,
        setLocalTime,
        applySearchFiltersToLocal,
        searchFilterLoading,
        setLocalMealType, } = useSearch()

    const addRecipeId = (recipeId: string, recipe: Recipe) => {
        let servingSize = 2; //default serving size
        if (recipe.metadata.minServing && recipe.metadata.minServing > 2) {
            servingSize = recipe.metadata.minServing;
        }
        if (recipeId)
            setSelectedRecipeIds((prevMap: Map<string, { servingSize: number }>) => {
                const newMap = new Map(prevMap);
                newMap.set(recipeId, { servingSize: servingSize });
                return newMap;
            });
    };


    const updateRecipeServingSize = (recipeId: string, servingSize: number) => {
        if (recipeId && servingSize)
            setSelectedRecipeIds((prevMap: Map<string, { servingSize: number }>) => {
                const newMap = new Map(prevMap);
                const recipeData = newMap.get(recipeId);
                newMap.set(recipeId, { ...recipeData, servingSize });

                return newMap;
            });
    };


    const removeRecipeId = (recipeId: string) => {
        setSelectedRecipeIds((prevMap: Map<string, { servingSize: number }>) => {
            const newMap = new Map(prevMap);
            newMap.delete(recipeId);
            return newMap;
        });
    };

    const addShoppingListId = async () => {
        const data = await addShoppingListForUser({
            variables:{
                user_id: user?.id
            }
        })
        const shoppingListId = data?.data?.insert_User_To_Shopping_List_one?.shopping_list_id;
        if(shoppingListId)
            dispatch(setGroceryListId(shoppingListId))
        else{
            console.error("Error generating shopping list id")
        }

        return shoppingListId;
    }

    const addData = async () => {
        if (selectedRecipeIds.size <= 0) return;

        setAddingMealToDB(true);
        let shoppingListId;
        if(!groceryListId) shoppingListId = await addShoppingListId();
        let mealPlan;
        try {
            if (!mealPlanId) {
                mealPlan = await addMealPlanDB({
                    variables: {
                        title: `${user?.nickName}'s Meal Plan`,
                        description: "My first meal plan",
                        author: user?.id,
                        author_profile_Image: user?.photoURL ?? null
                    }
                });

                const data = await addShoppingListForUser({
                    variables: {
                        user_id: user?.id
                    }
                })
                const shoppingListId = data?.data?.insert_User_To_Shopping_List_one?.shopping_list_id;
                if (shoppingListId)
                    dispatch(setGroceryListId(shoppingListId))
                else {
                    console.error("Error generating shopping list id")
                }

                const mealPlanID = mealPlan?.data?.insert_Meal_Plan_one?.id;

                if (mealPlanID) {
                    logRecipeEdit("MEAL_PLAN_CREATED", {
                        meal_plan_id: mealPlanID,
                    })

                    await addUserToMealPlanDB({
                        variables: {
                            meal_plan_id: mealPlanID,
                            user_id: user?.id
                        }
                    });
                    setMealPlanId(mealPlanID);
                } else {
                    throw new Error('Meal plan creation failed');
                }
            }

            const currentMealPlanId = mealPlanId || mealPlan?.data?.insert_Meal_Plan_one?.id;
            const scheduledTime = getMealTime(selectedMealType!, selectedDate);

            const queryVariables = [];
            for (const [recipeId, recipeData] of selectedRecipeIds) {
                const queryVariable = {
                    meal_plan_id: currentMealPlanId,
                    recipe_id: recipeId,
                    scheduled_date_time: scheduledTime,
                    serving_size: recipeData.servingSize ?? 2,
                    status: "pending",
                    meal_type: selectedMealType,
                }
                queryVariables.push(queryVariable);
            }

            if (queryVariables.length) {
                const { data: { insert_Meal: { returning: addedMealReturnData } } } = await addMealsToDB({
                    variables: {
                        meals: queryVariables,
                    }
                });
                await updateGroceryListFromAddingMeal(addedMealReturnData, shoppingListId);

                logRecipeEdit("ADDED_MEAL", {
                    meal_plan_id: currentMealPlanId,
                    eventData: {
                        meals: queryVariables,
                    },
                })
            }
        } catch (error) {
            console.error("Error while adding data:", error);
        } finally {
            setAddingMealToDB(false);
            history.replace('/meal-plan');
        }
    };


    const onBack = () => {
        if (history.length > 1) {
            history.goBack()
        } else {
            history.push('/meal-plan')
        }
    }

    const debouncedQuerySearchService = debounce((queryString: string) => setRecipeList(queryString), 500)
    const typeHandler = useCallback((queryString: string) => {
        setSearchString(queryString);
        if (!isSearchModalOpen) setIsSearchModalOpen(true)
        debouncedQuerySearchService(queryString)
        if (!queryString) {
            setIsSearchModalOpen(false)
        }
    }, [isSearchModalOpen]);


    return (
        <Grid className={styles.search_wrapper}>
            <Grid className={styles.search_container}>
                <Grid className={styles.back_button}>
                    <ButtonBase><GoBackIcon onClick={onBack} /></ButtonBase>
                    <Typography variant='caption4LightOutfit'>Select food for</Typography>
                </Grid>
                <Grid className={styles.search_header}>
                    <Typography variant='body1Outfit'>{formatMealDate(selectedMealType!, selectedDate)}</Typography>
                </Grid>
                <Grid className={styles.search_input_container}>
                    <SearchRecipeIcon />
                    <TextField
                        color='secondary'
                        onChange={(e) => typeHandler(e.target.value)}
                        placeholder='  Search for a recipe / meal plan here...'
                        className={styles.search_input}
                        sx={{
                            '& .MuiOutlinedInput-root': {

                                width: "100%",
                                '& fieldset': {
                                    border: 'none',
                                },
                                '&:hover fieldset': {
                                    border: 'none',
                                },
                                '&.Mui-focused fieldset': {
                                    border: 'none',
                                },
                            },

                        }}
                    />
                </Grid>
                <Filter setIsSearchModalOpen={setIsSearchModalOpen} searchFilterLoading={searchFilterLoading} applySearchFiltersToLocal={applySearchFiltersToLocal} localSelected={localSelected} setLocalCuisine={setLocalCuisine} setLocalIngredient={setLocalIngredient} setLocalTime={setLocalTime} setLocalMealType={setLocalMealType} applySearchFilters={applySearchFilters} clearFilter={clearFilter} handleDrawerOpen={handleDrawerOpen} handleDrawerClose={handleDrawerClose} searchFilters={searchFilters} drawerOpen={drawerOpen} />
            </Grid>

            {searchQueryString && !searchFilterLoading && recipeList.length <= 0 ?
                <Grid className={styles.empty_result_text}>
                    <Typography variant='caption4LightOutfit'>No results found</Typography>
                </Grid> : null}

            {isSearchModalOpen ?
                <Grid className={styles.recipe_card_container}>
                    <Grid>
                        {recipeList?.map((recipe: any) =>
                            <SearchResultCard key={recipe?.id} recipe={recipe} selectedRecipeIds={selectedRecipeIds} addRecipeId={() => addRecipeId(recipe.id, recipe)} updateRecipeServingSize={updateRecipeServingSize} />

                        )}
                    </Grid>
                </Grid>
                : null}

            {selectedRecipeIds.size > 0 ?
                <ButtonBase className={styles.add_meal_popup_container} onClick={addData}>
                    <Grid className={styles.add_meal_popup_text}>
                        <Typography variant='caption4LightOutfit'>{selectedMealType}</Typography>
                        <Typography variant='caption4LightOutfit' color={"rgba(255, 255, 255, 0.60)"}>{selectedRecipeIds.size} Recipes</Typography>
                    </Grid>
                    <RightArrow />
                </ButtonBase>
                : null}

            <Loader loading={addingMealToDB} />

        </Grid>
    );
};

export default RecipeForm;